import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Alert, Button } from '@truecare/ui';
import { ApiError } from '@truecare/api-client';
import { api } from '../api';
import { PortalShell } from '../components/PortalShell';
import {
  resolveDefaultPostSignInDestination,
  resolveNextRedirect,
} from '../lib/next-redirect';

// Token comes from ?token=… in the magic-link email URL.
// Phase 14a-2 — optional `next` echoed back from the magic-link URL so
// post-verify lands on the originally-requested admin URL when present.
const SearchSchema = z.object({
  token: z.string().min(32),
  next: z.string().optional(),
});

export const Route = createFileRoute('/sign-in_/verify')({
  validateSearch: (search) => SearchSchema.parse(search),
  component: VerifyPage,
});

/**
 * Magic-link verify page. Mounted when the caregiver clicks the link
 * in their welcome / sign-in email. JS extracts the token from the
 * URL and POSTs to /api/v1/auth/magic-link/verify — the POST is what
 * mutates state, so email-scanners that pre-fetch the URL just see
 * this static page, never consuming the token (system design §8.1).
 */
function VerifyPage() {
  const { token, next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Run-once guard. The magic-link token is single-use, so the /verify POST
  // must fire exactly once per page load. Without this, React 18 strict mode's
  // double-invoke (dev) — or any effect re-run — fires /verify twice: the first
  // consumes the token + creates the session (sign-in SUCCEEDS), the second sees
  // it already used and shows a false "link invalid" error. A ref persists
  // across strict-mode's double-invoke on the same instance; a genuine
  // remount/navigation gets a fresh ref and correctly re-verifies.
  const verifyStarted = useRef(false);
  // `mounted` guards state updates / navigation against a REAL unmount mid-
  // request. Each effect invocation re-sets it true, so React strict mode's
  // spurious cleanup (fired between its two dev invocations) does NOT cancel the
  // single ref-guarded run. Using a plain `cancelled` closure flag here instead
  // hangs the page on "Signing you in…": the first run's cleanup sets it, and
  // the ref guard blocks the second run, so the only real run's continuation is
  // cancelled and never navigates.
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    if (verifyStarted.current) {
      return () => {
        mounted.current = false;
      };
    }
    verifyStarted.current = true;
    void (async () => {
      try {
        const { error: respErr } = await api.POST('/api/v1/auth/magic-link/verify', {
          body: { token },
        });
        if (!mounted.current) return;
        if (respErr) {
          throw new ApiError('Magic-link verify failed', 0, respErr);
        }
        // Phase 14b-3 audit C2 — same TOTP-gate as the password sign-in
        // handler. Magic-link is one factor; if the user has TOTP
        // enrolled (operators always do), the session needs the second
        // factor before admin-side calls succeed.
        const meResp = await api.GET('/api/v1/auth/me', {});
        if (!mounted.current) return;
        // Seed React Query's ['auth','me'] with the freshly-authenticated user,
        // mirroring the password path (SignInForms.tsx). Without this, the
        // signed-out `null` cached on this verify page persists across the
        // client-side navigate to /me, so PortalShell's top-right chrome stays
        // blank until a full-page refresh. (The password path already seeds it;
        // the operator path full-reloads into the admin app so never hit this.)
        queryClient.setQueryData(['auth', 'me'], meResp.data ?? null);
        if (meResp.data?.totpEnrolled === true) {
          await navigate({
            to: '/sign-in/totp',
            search:
              next !== undefined && next !== null && next.length > 0
                ? { next }
                : {},
          });
          return;
        }

        // Phase 14a-2 — honour `next` only when allowlisted.
        const target = resolveNextRedirect(next ?? null);
        if (target) {
          window.location.href = target;
          return;
        }
        // Phase 14a-2 follow-up — persona-aware default destination.
        // Operator-only users land in the admin app (VITE_ADMIN_URL);
        // customer personas stay on /me.
        const dest = resolveDefaultPostSignInDestination(
          meResp.data?.roles ?? [],
        );
        if (dest.startsWith('http')) {
          window.location.href = dest;
        } else {
          await navigate({ to: dest });
        }
      } catch (err) {
        if (!mounted.current) return;
        setError(
          err instanceof Error && err.message
            ? 'This sign-in link is invalid or has expired.'
            : 'Something went wrong. Please request a new sign-in link.',
        );
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, [token, next, navigate]);

  return (
    <PortalShell signedOutSurface>
      <div className="mx-auto max-w-md px-6 pt-20 pb-16">
        {error ? (
          <>
            <Alert tone="danger" title="Sign-in link invalid">
              {error}
            </Alert>
            <Link to="/sign-in">
              <Button variant="primary" className="mt-6 w-full">
                Request a new link
              </Button>
            </Link>
          </>
        ) : (
          <div className="text-center">
            <h1 className="font-heading text-2xl text-forest">Signing you in…</h1>
            <p className="mt-3 font-body text-sm text-ink-muted">
              One moment while we verify your link.
            </p>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
